import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { X, ChevronRight, ChevronLeft, LayoutDashboard, ClipboardList, FileText, Receipt, Warehouse, AlertTriangle } from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'dashboard',
    title: 'Master Dashboard',
    description: 'Your command centre. See revenue, stock levels, alerts, and performance across all regions at a glance.',
    icon: LayoutDashboard,
    path: '/dashboard/master',
    color: '#B99A65',
  },
  {
    id: 'orders',
    title: 'UK Orders',
    description: 'Manage distributor orders from placement through approval, dispatch, and delivery tracking.',
    icon: ClipboardList,
    path: '/dashboard/uk-orders',
    color: '#3B82F6',
  },
  {
    id: 'quotes',
    title: 'Quote Builder',
    description: 'Create professional quotes with tick-box product selection, then convert approved quotes directly into orders.',
    icon: FileText,
    path: '/dashboard/quotes',
    color: '#8B5CF6',
  },
  {
    id: 'invoices',
    title: 'Invoice Engine',
    description: 'Auto-generated invoices with VAT, payment tracking, and Dispatch Lock — orders cannot ship until paid.',
    icon: Receipt,
    path: '/dashboard/uk-invoices',
    color: '#10B981',
  },
  {
    id: 'warehouse',
    title: 'UK Warehouse',
    description: 'Real-time stock levels, low-stock alerts, and factory order management for your UK operation.',
    icon: Warehouse,
    path: '/dashboard/uk-warehouse',
    color: '#F59E0B',
  },
  {
    id: 'alerts',
    title: 'Alerts & Actions',
    description: 'Critical alerts surface automatically — overdue invoices, low stock, pending approvals. Nothing slips through.',
    icon: AlertTriangle,
    path: '/dashboard/master',
    color: '#EF4444',
  },
];

const TOUR_KEY = 'rvk_tour_completed';

export default function GuidedTour() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Show tour for demo users who haven't completed it (CEO/admin view only)
    if (user?.is_demo && !sessionStorage.getItem(TOUR_KEY)) {
      // Skip tour for non-CEO demo views (salon, distributor, rep have their own dashboards)
      const viewAs = user?.demo_view_as || 'founder';
      if (viewAs !== 'founder' && viewAs !== 'admin') return;
      const timer = setTimeout(() => setActive(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const completeTour = () => {
    sessionStorage.setItem(TOUR_KEY, 'true');
    setActive(false);
  };

  const goToStep = (idx) => {
    setStep(idx);
    navigate(TOUR_STEPS[idx].path);
  };

  if (!active) return null;

  const current = TOUR_STEPS[step];
  const Icon = current.icon;
  const isLast = step === TOUR_STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[9998] pointer-events-none" data-testid="guided-tour">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 pointer-events-auto" onClick={completeTour} />

      {/* Tour Card */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[90vw] max-w-md pointer-events-auto" data-testid="tour-card">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden">
          {/* Progress bar */}
          <div className="h-1 bg-gray-100">
            <div
              className="h-full transition-all duration-500"
              style={{ width: `${((step + 1) / TOUR_STEPS.length) * 100}%`, backgroundColor: current.color }}
            />
          </div>

          <div className="p-5">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${current.color}15` }}>
                  <Icon className="w-5 h-5" style={{ color: current.color }} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 font-medium">Step {step + 1} of {TOUR_STEPS.length}</div>
                  <h3 className="text-base font-bold text-[#2B1F27]">{current.title}</h3>
                </div>
              </div>
              <button
                onClick={completeTour}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="tour-skip"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Description */}
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">{current.description}</p>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={completeTour}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="tour-skip-text"
              >
                Skip tour
              </button>

              <div className="flex items-center gap-2">
                {step > 0 && (
                  <button
                    onClick={() => goToStep(step - 1)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg transition-colors"
                    data-testid="tour-prev"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    Back
                  </button>
                )}
                <button
                  onClick={() => isLast ? completeTour() : goToStep(step + 1)}
                  className="flex items-center gap-1 px-4 py-1.5 text-sm text-white rounded-lg transition-colors font-medium"
                  style={{ backgroundColor: current.color }}
                  data-testid="tour-next"
                >
                  {isLast ? 'Finish' : 'Next'}
                  {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            {/* Step dots */}
            <div className="flex items-center justify-center gap-1.5 mt-4">
              {TOUR_STEPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToStep(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === step ? 'w-6' : 'hover:opacity-80'}`}
                  style={{ backgroundColor: i === step ? current.color : '#D1D5DB' }}
                  data-testid={`tour-dot-${i}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
